@use 'sass:map';
@use '../../scss/global'as *;
@use '../../scss/components/form-element.mixins' as *;
@use 'themes';

// ================================================================ //
// -- Local Variables

// ================================================================ //
// -- Mixins

@mixin pill-focus-indicator {
  max-width: calc(100% - (var(--G-form-element-height) / 2 + 0.5em));
  left: calc(var(--G-form-element-height) / 2 - 0.5em);
}

// ================================================================ //
// -- Component Styles

:host {
  display: block;

  .fluid-search-box {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    column-gap: 16px; // Carryover from previous FLUID Grid instance
  }

  .search-box-dropdown {
    display: block;
  }

  @include focus-indicator($input-styles: false);

  // ==== Input Field & Select Container

  .search-container {
    flex: 1 1 100%;
    position: relative;

    &.with-criteria {
      width: 100%;
      display: flex;
      align-items: center;
      column-gap: 1em;
      border-width: var(--G-border-width);
      border-style: solid;
      border-color: var(--G-form-element-border-color);
      border-radius: var(--G-border-radius);
      background-color: rgba($white, 0.75);
      box-shadow: var(--G-form-element-box-shadow);

      @include transition(
        $properties: 'background-color, border-color',
        $duration: short
      );

      &.pill-search-box,
      &.header-search-box {
        border-radius: calc(var(--G-form-element-height) / 2);

        .form-field-focus-indicator {
          @include pill-focus-indicator;
        }
      }

      &.header-search-box {
        border-color: transparent;
      }

      &:hover,
      &:focus,
      &:focus-within {
        background-color: $white;
        border-color: var(--G-form-element-border-color-hover);
      }

      ::part(fluid-input-field focus-indicator) {
        display: none;
      }

      & > fluid-select {
        flex: 0 1 auto;
        position: relative;

        // Creates a small border on the left side of the search box
        &::after {
          width: 1px;
          height: 60%;
          position: absolute;
          right: -0.5em;
          top: 50%; // Start from the middle
          transform: translateY(-50%); // Center vertically
          background-color: var(--G-form-element-border-color);
          content: '';
        }
      }

      & > fluid-input-field,
      & > fluid-context-menu {
        flex: 1 1 auto;
      }
    }

    @include screen-md {
      min-width: 240px; // Smallest width above largest user-agent default for <input>
    }
  }

  // ============================================ //
  // -- FLUID Input Field Style Overrides

  //Styles for Search Boxes with criteria
  .default-search-box.with-criteria,
  .header-search-box.with-criteria,
  .pill-search-box.with-criteria {
    ::part(fluid-input-field form-container) {
      min-height: calc(var(--G-form-element-height) - var(--G-border-width) * 2); // Offset parent border
      border: none;
      box-shadow: none;
      background-color: transparent;
    }
  }

  // Additional styles for header and pill search boxes with criteria
  .header-search-box.with-criteria,
  .pill-search-box.with-criteria {
    ::part(fluid-input-field form-container) {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }

  // Additional styles for header remove the background color and control from wrapper above
  .header-search-box.with-criteria {
    ::part(fluid-input-field form-container) {
      background-color: transparent;
    }
  }

  // Styles for all header / pill search boxes
  .header-search-box,
  .pill-search-box {
    ::part(fluid-input-field form-container) {
      border-radius: calc(var(--G-form-element-height) / 2);
      padding-right: calc(var(--G-form-element-button-offset) + (var(--G-form-element-button-size) * 2) + 1em);
    }
  }

  // Styles for header / pill search boxes without criteria
  .header-search-box:not(.with-criteria),
  .pill-search-box:not(.with-criteria) {
    ::part(fluid-input-field form-container) {
      padding-left: calc(var(--G-form-element-padding-left) * 2);
    }
  }

  // Styles for header search box without criteria
  .header-search-box:not(.with-criteria) {
    ::part(fluid-input-field form-container) {
      background-color: rgba($white, 0.75);

      &:not(:hover):not(:focus-within) {
        border-color: transparent;
      }

      &:hover,
      &:focus-within {
        background-color: $white;
      }

      @include transition(
        $properties: 'background-color, border-color',
        $duration: short
      );
    }
  }

  // ============================================ //
  // -- FLUID Select Style Overrides

  // <select> Element
  ::part(fluid-select select) {
    height: calc(var(--G-form-element-height) - var(--G-border-width) * 2); // Offset parent border
    min-height: calc(var(--G-form-element-height) - var(--G-border-width) * 2); // Offset parent border
    border: none;
    background-size: 0;
    box-shadow: none;
    background-color: transparent;
    padding-left: calc(var(--G-form-element-padding-left) * 2);
    padding-right: calc(var(--G-form-element-button-offset) + (var(--G-form-element-button-size) * 2));
  }

  // <select> Element with criteria - Pill Shape
  .header-search-box.with-criteria,
  .pill-search-box.with-criteria {
    ::part(fluid-select select) {
      border-top-left-radius: calc(var(--G-form-element-height) / 2);
      border-bottom-left-radius: calc(var(--G-form-element-height) / 2);
    }
  }

  // ============================================ //
  // -- Additional Form Element Style Overrides

  // Styles for adornment part
  ::part(fluid-input-field adornment) {
    right: calc(var(--G-form-element-padding-right) + 0.5em);
  }

  // Styles for focus-indicator part
  .header-search-box,
  .pill-search-box {
    ::part(fluid-input-field focus-indicator) {
      @include pill-focus-indicator;
    }
  }

  // Styles to offset "cancel" button when adornment is present
  .header-search-box,
  .pill-search-box {
    ::part(fluid-input-field cancel-button) {
      right: var(--offset-cancel-button);
    }
  }

  // ============================================ //
  // -- Search Button Container

  .search-button {
    flex: 0 0 auto;

    // Offset Form Label height (keeps search button vertically centered)
    &.offset-form-label {
      padding-top: calc(var(--G-font-size) * var(--G-form-label-line-height));
    }
  }

  // ============================================ //
  // -- "helpText" Popover

  .info-popover {
    position: absolute;
    bottom: calc((var(--G-form-element-height) / 2) - calc(map.get($icon-sizes, xs) / 2));
    right: calc(var(--G-form-element-button-size) + var(--G-form-element-button-offset) + 3em);
  }
}
